import React, { useState, useEffect, useCallback, useRef } from 'react';
import WeatherForecast from '../components/WeatherForecast';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import { updateBooking } from '../../supabase/bookings';
import { uploadJobPhoto, deleteJobPhoto } from '../../supabase/storage';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import { ArrowLeft, Upload, Trash2, CheckSquare, Square, ExternalLink, FileText, Loader2, CheckCircle, X, Share2, RefreshCw, Timer, Square as StopIcon, RotateCcw, Gauge, Monitor, AlertTriangle, MessageSquare, Pencil } from 'lucide-react';
import { MAINTENANCE_ADD_ONS, MAINTENANCE_PRICING, SITUATION_CHARGES, isSuvOrTruck, toET } from '../../data/constants';

// iOS wants "sms:+1XXX&body=...", Android/others want "sms:+1XXX?body=..." — same
// pattern used for the raffle consolation texts in admin/pages/Raffle.jsx.
const SMS_BODY_DELIM = /iPhone|iPad|iPod/.test(navigator.userAgent) ? '&' : '?';

function toE164(phone) {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  return phone || '';
}

const GOOGLE_REVIEW_URL = 'https://g.page/r/CXdr5n1W7IbjEBM/review';

function invoiceSmsHref(booking, payPageUrl) {
  const firstName = (booking.client_name || 'there').trim().split(' ')[0];
  const serviceName = booking.tier || booking.service_type || 'detail';
  // Same fallback formula used by the admin invoice modal and the pay page
  // itself — a quick estimate for the text, not the billing source of truth.
  const depositCredit = Number(booking.deposit_amount) || (booking.booking_type === 'maintenance' ? 50 : 0);
  const balance = Math.max(0, (Number(booking.price) || 0) - depositCredit);
  const body = encodeURIComponent(
    `Hey ${firstName}! Your ${serviceName} is complete — thank you for choosing Monarch Detailing!\n\n`
    + `You can view your invoice and add a tip here: ${payPageUrl}\n`
    + `Remaining balance: $${balance.toFixed(2)}\n\n`
    + `If you enjoyed the service, a quick Google review means the world: ${GOOGLE_REVIEW_URL}\n\n`
    + `Thank you so much for trusting Monarch Detailing with your vehicle!\n\n`
    + `Monarch Detailing — Detailed with Precision, Delivered with Pride. 🦋`
  );
  return `sms:${toE164(booking.phone)}${SMS_BODY_DELIM}body=${body}`;
}

const CHECKLIST = [
  'Pre-inspection walkthrough',
  'Exterior wash & rinse',
  'Wheel & tire cleaning',
  'Clay bar treatment',
  'Paint sealant applied',
  'Windows cleaned (in/out)',
  'Vacuum interior',
  'Dashboard & panels wiped',
  'Air freshener placed',
  'Final quality check',
];

const STATUS_OPTIONS = ['scheduled', 'in-progress', 'completed', 'cancelled'];

const SUPABASE_STORAGE_PREFIX = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/job-photos/`;

function PhotoDropzone({ bookingId, phase, photos, onUpload, onDelete }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const onDrop = useCallback(async (accepted) => {
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of accepted) {
        const { url } = await uploadJobPhoto(bookingId, phase, file);
        onUpload(phase, url);
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed — check Supabase storage bucket');
    } finally {
      setUploading(false);
    }
  }, [bookingId, phase, onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: true,
  });

  return (
    <div>
      <h4 className="font-medium text-gray-700 text-sm mb-2 capitalize">{phase} Photos</h4>
      <div className="grid grid-cols-3 gap-2 mb-2">
        {(photos || []).map((url, i) => (
          <div key={i} className="relative group aspect-square">
            <img src={url} alt="" className="w-full h-full object-cover rounded-lg" />
            <button
              onClick={() => onDelete(phase, url)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 text-center text-sm cursor-pointer transition-colors ${
          isDragActive ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-gray-200 hover:border-[#C9A84C]'
        }`}
      >
        <input {...getInputProps()} />
        <Upload size={20} className="mx-auto text-gray-400 mb-1" />
        {uploading ? (
          <p className="text-gray-500">Uploading…</p>
        ) : (
          <p className="text-gray-400">{isDragActive ? 'Drop here' : 'Drag & drop or click'}</p>
        )}
      </div>
      {uploadError && (
        <p className="text-xs text-red-500 mt-1.5 font-medium">{uploadError}</p>
      )}
    </div>
  );
}

function InspectionDropzone({ bookingId, category, label, hint, icon, photos, onUpload, onDelete, maxPhotos }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const onDrop = useCallback(async (accepted) => {
    const allowed = maxPhotos ? accepted.slice(0, maxPhotos - (photos || []).length) : accepted;
    if (!allowed.length) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of allowed) {
        const { url } = await uploadJobPhoto(bookingId, `inspection_${category}`, file);
        onUpload(category, url);
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed — check Supabase storage bucket');
    } finally {
      setUploading(false);
    }
  }, [bookingId, category, photos, onUpload, maxPhotos]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: !maxPhotos || maxPhotos > 1,
  });

  const atLimit = maxPhotos && (photos || []).length >= maxPhotos;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-400">{icon}</span>
        <h4 className="font-semibold text-gray-800 text-sm">{label}</h4>
        {hint && <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">{hint}</span>}
      </div>
      {(photos || []).length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {photos.map((url, i) => (
            <div key={i} className="relative group aspect-square">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt="" className="w-full h-full object-cover rounded-lg" />
              </a>
              <button
                onClick={() => onDelete(category, url)}
                className="absolute top-1 right-1 bg-black/60 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      {!atLimit && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-3 text-center text-sm cursor-pointer transition-colors ${
            isDragActive ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
          }`}
        >
          <input {...getInputProps()} />
          <Upload size={16} className="mx-auto text-gray-400 mb-1" />
          {uploading ? (
            <p className="text-gray-500 text-xs">Uploading…</p>
          ) : (
            <p className="text-gray-400 text-xs">
              {isDragActive ? 'Drop here' : maxPhotos === 1 ? 'Tap to capture' : 'Drag & drop or tap'}
            </p>
          )}
        </div>
      )}
      {uploadError && (
        <p className="text-xs text-red-500 mt-1.5 font-medium">{uploadError}</p>
      )}
    </div>
  );
}

// Maintenance modal — full itemized invoice builder
function MaintenanceInvoiceModal({ booking, onConfirm, onSkip, onCancel }) {
  const isSuv = isSuvOrTruck(booking.make, booking.model);
  const isFirstVisit = booking.service_type === 'Alchemist Deep Clean';

  const [serviceType, setServiceType] = useState(isFirstVisit ? 'alchemistDeepClean' : 'monthly');
  const [vehicleType, setVehicleType] = useState(isSuv ? 'suvTruck' : 'sedan');
  const [selectedAddOns, setSelectedAddOns] = useState(() => {
    if (!booking.add_ons) return [];
    return booking.add_ons.split(',').map(s => s.trim()).filter(Boolean);
  });
  const [deductDeposit, setDeductDeposit] = useState(isFirstVisit);
  const [sending, setSending] = useState(false);
  const [priceOverride, setPriceOverride] = useState('');
  const [situationCharges, setSituationCharges] = useState([]);
  const [tip, setTip] = useState('');

  const basePrice = MAINTENANCE_PRICING[serviceType][vehicleType];
  const addOnsTotal = selectedAddOns.reduce((sum, id) => {
    const a = MAINTENANCE_ADD_ONS.find(x => x.id === id);
    return sum + (a ? a.price : 0);
  }, 0);
  const situationTotal = situationCharges.reduce((sum, id) => {
    const s = SITUATION_CHARGES.find(x => x.id === id);
    return sum + (s ? s.price : 0);
  }, 0);
  const tipAmount = parseFloat(tip) || 0;
  const calculatedTotal = basePrice + addOnsTotal + situationTotal - (deductDeposit ? MAINTENANCE_PRICING.deposit : 0);
  // Override replaces the total SERVICE price — the deposit still comes off
  // automatically when the checkbox is on, so overriding for a discount can
  // never accidentally re-invoice the deposit on top of it.
  const overrideServicePrice = priceOverride !== '' ? (parseFloat(priceOverride) || 0) : null;
  const overrideBalance = overrideServicePrice !== null ? Math.max(0, overrideServicePrice - (deductDeposit ? MAINTENANCE_PRICING.deposit : 0)) : null;
  const baseTotal = overrideBalance !== null ? overrideBalance : calculatedTotal;
  const total = baseTotal + tipAmount;

  const toggleAddOn = (id) => setSelectedAddOns(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleSend = async () => {
    setSending(true);
    let lineItems;
    if (priceOverride !== '') {
      const serviceName = serviceType === 'alchemistDeepClean' ? 'Alchemist Deep Clean' : 'Monthly Maintenance';
      const vehicleStr = [booking.year, booking.make, booking.model].filter(Boolean).join(' ');
      lineItems = [
        { description: `${serviceName} — ${vehicleStr}`, amount: overrideServicePrice },
        ...(deductDeposit ? [{ description: '$50 Deposit (previously paid — applied to balance)', amount: -MAINTENANCE_PRICING.deposit }] : []),
        ...(tipAmount > 0 ? [{ description: 'Gratuity / Tip', amount: tipAmount }] : []),
      ];
    } else {
      lineItems = [
        {
          description: `${serviceType === 'alchemistDeepClean' ? 'Alchemist Deep Clean' : 'Monthly Maintenance'} — ${[booking.year, booking.make, booking.model].filter(Boolean).join(' ')} (${vehicleType === 'suvTruck' ? 'SUV/Truck' : 'Sedan'})`,
          amount: basePrice,
        },
        ...selectedAddOns.map(id => {
          const a = MAINTENANCE_ADD_ONS.find(x => x.id === id);
          return { description: a.label, amount: a.price };
        }),
        ...situationCharges.map(id => {
          const s = SITUATION_CHARGES.find(x => x.id === id);
          return { description: s.label, amount: s.price };
        }),
        ...(deductDeposit ? [{ description: '$50 Deposit (previously paid — applied to balance)', amount: -MAINTENANCE_PRICING.deposit }] : []),
        ...(tipAmount > 0 ? [{ description: 'Gratuity / Tip', amount: tipAmount }] : []),
      ];
    }
    await onConfirm(lineItems);
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Complete Job & Send Invoice</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Service type */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Service Type</p>
            <div className="flex gap-2">
              {[
                { key: 'alchemistDeepClean', label: 'Alchemist Deep Clean' },
                { key: 'monthly', label: 'Monthly Maintenance' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setServiceType(key); setDeductDeposit(key === 'alchemistDeepClean'); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    serviceType === key ? 'bg-[#C9A84C] border-[#C9A84C] text-black' : 'border-gray-200 text-gray-600 hover:border-[#C9A84C]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle type */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vehicle Type</p>
            <div className="flex gap-2">
              {[{ key: 'sedan', label: 'Sedan / Car' }, { key: 'suvTruck', label: 'SUV / Truck' }].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setVehicleType(key)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                    vehicleType === key ? 'bg-[#C9A84C] border-[#C9A84C] text-black' : 'border-gray-200 text-gray-600 hover:border-[#C9A84C]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Add-ons */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Add-Ons</p>
            <div className="grid grid-cols-2 gap-2">
              {MAINTENANCE_ADD_ONS.map(addon => {
                const on = selectedAddOns.includes(addon.id);
                return (
                  <button
                    key={addon.id}
                    onClick={() => toggleAddOn(addon.id)}
                    className={`text-left p-3 rounded-xl border transition-colors ${
                      on ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-gray-200 hover:border-[#C9A84C]/50'
                    }`}
                  >
                    <span className="text-lg block mb-1">{addon.emoji}</span>
                    <p className="text-xs font-semibold text-gray-800 leading-tight">{addon.label}</p>
                    <p className={`text-xs font-bold mt-0.5 ${on ? 'text-[#C9A84C]' : 'text-gray-400'}`}>+${addon.price}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Deposit deduction */}
          {serviceType === 'alchemistDeepClean' && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={deductDeposit}
                onChange={e => setDeductDeposit(e.target.checked)}
                className="w-4 h-4 accent-[#C9A84C]"
              />
              <span className="text-sm text-gray-700">Deduct $50 deposit (already paid at enrollment)</span>
            </label>
          )}

          {/* Situation charges */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Situation Charges</p>
            <div className="flex flex-col gap-2">
              {SITUATION_CHARGES.map(charge => {
                const on = situationCharges.includes(charge.id);
                return (
                  <button
                    key={charge.id}
                    onClick={() => setSituationCharges(prev =>
                      prev.includes(charge.id) ? prev.filter(x => x !== charge.id) : [...prev, charge.id]
                    )}
                    className={`text-left p-3 rounded-xl border transition-colors flex items-center gap-3 ${
                      on ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-red-300'
                    }`}
                  >
                    <span className="text-lg">{charge.emoji}</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">{charge.label}</p>
                      <p className="text-xs text-gray-500">{charge.desc}</p>
                    </div>
                    <span className={`text-sm font-bold shrink-0 ${on ? 'text-red-500' : 'text-gray-400'}`}>+${charge.price}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom price override */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Override Total Price <span className="font-normal normal-case text-gray-400">(before deposit — clears add-ons/situation charges above)</span></p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder={`${basePrice} (auto-calculated)`}
                value={priceOverride}
                onChange={e => setPriceOverride(e.target.value)}
                className="w-full pl-7 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A84C] text-gray-900"
              />
            </div>
            {overrideServicePrice !== null && deductDeposit && (
              <p className="text-xs text-emerald-600 mt-1.5">
                = ${overrideServicePrice.toFixed(2)} − ${MAINTENANCE_PRICING.deposit} deposit already paid = ${overrideBalance.toFixed(2)} balance due
              </p>
            )}
          </div>

          {/* Tip */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tip <span className="font-normal normal-case text-gray-400">(optional)</span></p>
            <div className="flex gap-2 mb-2">
              {[10, 15, 20].map(pct => {
                const amt = parseFloat((calculatedTotal * pct / 100).toFixed(2));
                const selected = parseFloat(tip) === amt;
                return (
                  <button
                    key={pct}
                    onClick={() => setTip(selected ? '' : amt.toFixed(2))}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                      selected ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-gray-200 text-gray-600 hover:border-[#C9A84C]/50'
                    }`}
                  >
                    {pct}%<br /><span className="text-xs font-normal">${amt.toFixed(2)}</span>
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Custom tip amount"
                value={tip}
                onChange={e => setTip(e.target.value)}
                className="w-full pl-7 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A84C] text-gray-900"
              />
            </div>
          </div>

          {/* Total */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-1.5">
            {overrideServicePrice !== null ? (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Total price (override)</span>
                <span>${overrideServicePrice.toFixed(2)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{serviceType === 'alchemistDeepClean' ? 'Alchemist Deep Clean' : 'Monthly Maintenance'}</span>
                  <span>${basePrice}</span>
                </div>
                {selectedAddOns.map(id => {
                  const a = MAINTENANCE_ADD_ONS.find(x => x.id === id);
                  return a ? (
                    <div key={id} className="flex justify-between text-sm text-gray-600">
                      <span>{a.label}</span>
                      <span>+${a.price}</span>
                    </div>
                  ) : null;
                })}
                {situationCharges.map(id => {
                  const s = SITUATION_CHARGES.find(x => x.id === id);
                  return s ? (
                    <div key={id} className="flex justify-between text-sm text-red-600">
                      <span>{s.label}</span>
                      <span>+${s.price}</span>
                    </div>
                  ) : null;
                })}
              </>
            )}
            {deductDeposit && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span>Deposit credit</span>
                <span>-${MAINTENANCE_PRICING.deposit}</span>
              </div>
            )}
            {tipAmount > 0 && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span>Gratuity / Tip</span>
                <span>+${tipAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2 mt-2">
              <span>Invoice Total</span>
              <span className="text-lg">${total}</span>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-gray-100 flex flex-col gap-2">
          <button
            onClick={handleSend}
            disabled={sending}
            className="w-full py-3 bg-[#C9A84C] text-black rounded-xl font-semibold text-sm hover:bg-[#b8943d] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            {sending ? 'Sending Invoice…' : `Complete & Send Invoice ($${total})`}
          </button>
          <button
            onClick={onSkip}
            className="w-full py-2.5 text-gray-500 text-sm hover:text-gray-800 transition-colors"
          >
            Complete without Invoice
          </button>
        </div>
      </div>
    </div>
  );
}

// Simplified modal for weekend and one-time bookings
function SimpleInvoiceModal({ booking, onConfirm, onSkip, onCancel }) {
  const depositPaid = Number(booking.deposit_amount) || 0;
  const bookingPrice = Number(booking.price) || 0;
  const defaultBalance = bookingPrice - depositPaid;

  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [priceOverride, setPriceOverride] = useState('');
  const [situationCharges, setSituationCharges] = useState([]);
  const [sending, setSending] = useState(false);
  const [tip, setTip] = useState('');

  const addOnsTotal = selectedAddOns.reduce((sum, id) => {
    const a = MAINTENANCE_ADD_ONS.find(x => x.id === id);
    return sum + (a ? a.price : 0);
  }, 0);
  const situationTotal = situationCharges.reduce((sum, id) => {
    const s = SITUATION_CHARGES.find(x => x.id === id);
    return sum + (s ? s.price : 0);
  }, 0);
  const tipAmount = parseFloat(tip) || 0;
  const calculatedTotal = defaultBalance + addOnsTotal + situationTotal;
  // Override replaces the total SERVICE price (same meaning as "Total booked
  // price" above) — the already-paid deposit still comes off automatically,
  // so overriding for a discount can never accidentally re-invoice the
  // deposit on top of it.
  const overrideServicePrice = priceOverride !== '' ? (parseFloat(priceOverride) || 0) : null;
  const overrideBalance = overrideServicePrice !== null ? Math.max(0, overrideServicePrice - depositPaid) : null;
  const baseTotal = overrideBalance !== null ? overrideBalance : calculatedTotal;
  const total = baseTotal + tipAmount;

  const toggleAddOn = (id) => setSelectedAddOns(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleSend = async () => {
    setSending(true);
    const serviceName = booking.tier || booking.service_type || 'Detailing Service';
    const vehicleStr = [booking.year, booking.make, booking.model].filter(Boolean).join(' ');

    let lineItems;
    if (priceOverride !== '') {
      lineItems = [
        { description: `${serviceName}${vehicleStr ? ` — ${vehicleStr}` : ''}`, amount: overrideServicePrice },
        ...(depositPaid > 0 ? [{ description: 'Deposit Paid (applied to balance)', amount: -depositPaid }] : []),
        ...(tipAmount > 0 ? [{ description: 'Gratuity / Tip', amount: tipAmount }] : []),
      ];
    } else {
      lineItems = [
        {
          description: `${serviceName}${vehicleStr ? ` — ${vehicleStr}` : ''} (balance due)`,
          amount: defaultBalance,
        },
        ...selectedAddOns.map(id => {
          const a = MAINTENANCE_ADD_ONS.find(x => x.id === id);
          return { description: a.label, amount: a.price };
        }),
        ...situationCharges.map(id => {
          const s = SITUATION_CHARGES.find(x => x.id === id);
          return { description: s.label, amount: s.price };
        }),
        ...(tipAmount > 0 ? [{ description: 'Gratuity / Tip', amount: tipAmount }] : []),
      ];
    }
    await onConfirm(lineItems);
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Complete Job & Send Invoice</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Pricing summary */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-gray-700">
              <span>Total booked price</span>
              <span className="font-semibold">${bookingPrice.toFixed(2)}</span>
            </div>
            {depositPaid > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Deposit paid</span>
                <span className="font-semibold">-${depositPaid.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-amber-800 border-t border-amber-200 pt-2 mt-1">
              <span>Balance due</span>
              <span>${defaultBalance.toFixed(2)}</span>
            </div>
          </div>

          {/* Add-ons */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Add-Ons <span className="font-normal normal-case text-gray-400">(optional)</span></p>
            <div className="grid grid-cols-2 gap-2">
              {MAINTENANCE_ADD_ONS.map(addon => {
                const on = selectedAddOns.includes(addon.id);
                return (
                  <button
                    key={addon.id}
                    onClick={() => toggleAddOn(addon.id)}
                    className={`text-left p-3 rounded-xl border transition-colors ${
                      on ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-gray-200 hover:border-[#C9A84C]/50'
                    }`}
                  >
                    <span className="text-lg block mb-1">{addon.emoji}</span>
                    <p className="text-xs font-semibold text-gray-800 leading-tight">{addon.label}</p>
                    <p className={`text-xs font-bold mt-0.5 ${on ? 'text-[#C9A84C]' : 'text-gray-400'}`}>+${addon.price}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Situation charges */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Situation Charges</p>
            <div className="flex flex-col gap-2">
              {SITUATION_CHARGES.map(charge => {
                const on = situationCharges.includes(charge.id);
                return (
                  <button
                    key={charge.id}
                    onClick={() => setSituationCharges(prev =>
                      prev.includes(charge.id) ? prev.filter(x => x !== charge.id) : [...prev, charge.id]
                    )}
                    className={`text-left p-3 rounded-xl border transition-colors flex items-center gap-3 ${
                      on ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-red-300'
                    }`}
                  >
                    <span className="text-lg">{charge.emoji}</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">{charge.label}</p>
                      <p className="text-xs text-gray-500">{charge.desc}</p>
                    </div>
                    <span className={`text-sm font-bold shrink-0 ${on ? 'text-red-500' : 'text-gray-400'}`}>+${charge.price}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom price override */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Override Total Price <span className="font-normal normal-case text-gray-400">(before deposit — clears add-ons/situation charges above)</span></p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder={`${bookingPrice.toFixed(2)} (auto-calculated)`}
                value={priceOverride}
                onChange={e => setPriceOverride(e.target.value)}
                className="w-full pl-7 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A84C] text-gray-900"
              />
            </div>
            {overrideServicePrice !== null && depositPaid > 0 && (
              <p className="text-xs text-emerald-600 mt-1.5">
                = ${overrideServicePrice.toFixed(2)} − ${depositPaid.toFixed(2)} deposit already paid = ${overrideBalance.toFixed(2)} balance due
              </p>
            )}
          </div>

          {/* Tip */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tip <span className="font-normal normal-case text-gray-400">(optional)</span></p>
            <div className="flex gap-2 mb-2">
              {[10, 15, 20].map(pct => {
                const amt = parseFloat((calculatedTotal * pct / 100).toFixed(2));
                const selected = parseFloat(tip) === amt;
                return (
                  <button
                    key={pct}
                    onClick={() => setTip(selected ? '' : amt.toFixed(2))}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                      selected ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-gray-200 text-gray-600 hover:border-[#C9A84C]/50'
                    }`}
                  >
                    {pct}%<br /><span className="text-xs font-normal">${amt.toFixed(2)}</span>
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Custom tip amount"
                value={tip}
                onChange={e => setTip(e.target.value)}
                className="w-full pl-7 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A84C] text-gray-900"
              />
            </div>
          </div>

          {/* Invoice total */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-1.5">
            {overrideServicePrice !== null ? (
              <>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Total price (override)</span>
                  <span>${overrideServicePrice.toFixed(2)}</span>
                </div>
                {depositPaid > 0 && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Deposit paid</span>
                    <span>-${depositPaid.toFixed(2)}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Balance due</span>
                  <span>${defaultBalance.toFixed(2)}</span>
                </div>
                {selectedAddOns.map(id => {
                  const a = MAINTENANCE_ADD_ONS.find(x => x.id === id);
                  return a ? (
                    <div key={id} className="flex justify-between text-sm text-gray-600">
                      <span>{a.label}</span>
                      <span>+${a.price}</span>
                    </div>
                  ) : null;
                })}
                {situationCharges.map(id => {
                  const s = SITUATION_CHARGES.find(x => x.id === id);
                  return s ? (
                    <div key={id} className="flex justify-between text-sm text-red-600">
                      <span>{s.label}</span>
                      <span>+${s.price}</span>
                    </div>
                  ) : null;
                })}
              </>
            )}
            {tipAmount > 0 && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span>Gratuity / Tip</span>
                <span>+${tipAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2 mt-2">
              <span>Invoice Total</span>
              <span className="text-lg">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-gray-100 flex flex-col gap-2">
          <button
            onClick={handleSend}
            disabled={sending}
            className="w-full py-3 bg-[#C9A84C] text-black rounded-xl font-semibold text-sm hover:bg-[#b8943d] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            {sending ? 'Sending Invoice…' : `Complete & Send Invoice ($${total.toFixed(2)})`}
          </button>
          <button
            onClick={onSkip}
            className="w-full py-2.5 text-gray-500 text-sm hover:text-gray-800 transition-colors"
          >
            Complete without Invoice
          </button>
        </div>
      </div>
    </div>
  );
}

export default function JobDetail() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState({});
  const [photos, setPhotos] = useState({ before: [], after: [] });
  const [inspectionPhotos, setInspectionPhotos] = useState({ odometer: [], dash: [], damage: [] });
  const [odometerReading, setOdometerReading] = useState('');
  const [saving, setSaving] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [resending, setResending] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [redoInvoice, setRedoInvoice] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [toast, setToast] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const [editingTimer, setEditingTimer] = useState(false);
  const [timerStartInput, setTimerStartInput] = useState('');
  const [timerEndInput, setTimerEndInput] = useState('');
  const [caption, setCaption] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [summarizingNotes, setSummarizingNotes] = useState(false);
  const notesRef = useRef(null);

  // Tick while timer is running
  useEffect(() => {
    if (!booking) return;
    const started = booking.timer_started_at;
    const ended = booking.timer_ended_at;
    if (started && !ended) {
      const tick = () => setElapsed(Math.floor((Date.now() - new Date(started).getTime()) / 1000));
      tick();
      timerRef.current = setInterval(tick, 1000);
    } else if (started && ended) {
      setElapsed(Math.floor((new Date(ended).getTime() - new Date(started).getTime()) / 1000));
    } else {
      setElapsed(0);
    }
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.timer_started_at, booking?.timer_ended_at]);

  const formatElapsed = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleStartTimer = async () => {
    const now = new Date().toISOString();
    await updateBooking(bookingId, { timer_started_at: now, timer_ended_at: null });
    setBooking(prev => ({ ...prev, timer_started_at: now, timer_ended_at: null }));
  };

  const handleStopTimer = async () => {
    const now = new Date().toISOString();
    await updateBooking(bookingId, { timer_ended_at: now });
    setBooking(prev => ({ ...prev, timer_ended_at: now }));
    clearInterval(timerRef.current);
  };

  const handleResetTimer = async () => {
    await updateBooking(bookingId, { timer_started_at: null, timer_ended_at: null });
    setBooking(prev => ({ ...prev, timer_started_at: null, timer_ended_at: null }));
    setElapsed(0);
  };

  // Manual correction for when Start/Stop got forgotten — keeps the original
  // calendar date, only lets the clock time change.
  const openTimerEdit = () => {
    setTimerStartInput(booking.timer_started_at ? format(new Date(booking.timer_started_at), 'HH:mm') : '');
    setTimerEndInput(booking.timer_ended_at ? format(new Date(booking.timer_ended_at), 'HH:mm') : '');
    setEditingTimer(true);
  };

  const handleSaveTimerEdit = async () => {
    const baseDate = booking.timer_started_at ? new Date(booking.timer_started_at) : new Date();
    const buildTimestamp = (timeStr) => {
      if (!timeStr) return null;
      const [h, m] = timeStr.split(':').map(Number);
      const d = new Date(baseDate);
      d.setHours(h, m, 0, 0);
      return d.toISOString();
    };
    const newStart = buildTimestamp(timerStartInput);
    const newEnd = buildTimestamp(timerEndInput);
    await updateBooking(bookingId, { timer_started_at: newStart, timer_ended_at: newEnd });
    setBooking(prev => ({ ...prev, timer_started_at: newStart, timer_ended_at: newEnd }));
    setEditingTimer(false);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    supabase.from('bookings').select('*').eq('id', bookingId).single()
      .then(({ data }) => {
        if (data) {
          setBooking(data);
          setChecklist(data.checklist || {});
          setPhotos({
            before: data.photos_before || [],
            after: data.photos_after || [],
          });
          setInspectionPhotos(data.photos_inspection || { odometer: [], dash: [], damage: [] });
          setOdometerReading(data.odometer_reading || '');
        }
        if (data) {
          setInvoiceUrl(data.invoice_url || null);
          setInvoiceStatus(data.invoice_status || null);
        }
        setLoading(false);
      });

    const channel = supabase
      .channel(`booking-${bookingId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'bookings', filter: `id=eq.${bookingId}` },
        (payload) => setBooking(payload.new))
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [bookingId]);

  const handleStatusChange = async (status) => {
    const bookingType = booking?.booking_type;
    const needsInvoiceModal =
      status === 'completed' &&
      !booking?.invoice_status &&
      (bookingType === 'maintenance' || bookingType === 'weekend' || bookingType === 'one_time');
    if (needsInvoiceModal) {
      setShowCompleteModal(true);
      return;
    }
    setSaving(true);
    try {
      await updateBooking(bookingId, { status });
      await supabase.from('activity_log').insert({
        note: `Job ${status === 'completed' ? 'completed' : `status → ${status}`}: ${booking.client_name} — ${booking.tier || booking.service_type}`,
        type: status === 'completed' ? 'job_completed' : 'status_change',
        client_id: booking.client_id || null,
      });
    } finally { setSaving(false); }
  };

  const handleCompleteWithInvoice = async (lineItems) => {
    setSaving(true);
    try {
      await updateBooking(bookingId, { status: 'completed' });
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch('/api/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bookingId, lineItems, regenerate: redoInvoice }),
      });
      const data = await res.json();
      if (res.ok) {
        setInvoiceUrl(data.invoiceUrl);
        setInvoiceStatus(data.status);
        if (redoInvoice) showToast('Invoice corrected and resent.');
      } else {
        alert(`Invoice error: ${data.error}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSaving(false);
      setShowCompleteModal(false);
      setRedoInvoice(false);
    }
  };

  const handleCompleteWithoutInvoice = async () => {
    setShowCompleteModal(false);
    setSaving(true);
    try {
      await updateBooking(bookingId, { status: 'completed' });
      await supabase.from('activity_log').insert({
        note: `Job completed: ${booking.client_name} — ${booking.tier || booking.service_type}`,
        type: 'job_completed',
        client_id: booking.client_id || null,
      });
    } finally { setSaving(false); }
  };

  const toggleCheck = async (item) => {
    const updated = { ...checklist, [item]: !checklist[item] };
    setChecklist(updated);
    await updateBooking(bookingId, { checklist: updated });
  };

  const handleUpload = async (phase, url) => {
    const updated = { ...photos, [phase]: [...(photos[phase] || []), url] };
    setPhotos(updated);
    await updateBooking(bookingId, {
      photos_before: updated.before,
      photos_after: updated.after,
    });
  };

  const handleDelete = async (phase, url) => {
    const path = url.replace(SUPABASE_STORAGE_PREFIX, '');
    try { await deleteJobPhoto(path); } catch {}
    const updated = { ...photos, [phase]: photos[phase].filter(u => u !== url) };
    setPhotos(updated);
    await updateBooking(bookingId, {
      photos_before: updated.before,
      photos_after: updated.after,
    });
  };

  const handleInspectionUpload = async (category, url) => {
    const updated = { ...inspectionPhotos, [category]: [...(inspectionPhotos[category] || []), url] };
    setInspectionPhotos(updated);
    await updateBooking(bookingId, { photos_inspection: updated });
  };

  const handleInspectionDelete = async (category, url) => {
    const path = url.replace(SUPABASE_STORAGE_PREFIX, '');
    try { await deleteJobPhoto(path); } catch {}
    const updated = { ...inspectionPhotos, [category]: (inspectionPhotos[category] || []).filter(u => u !== url) };
    setInspectionPhotos(updated);
    await updateBooking(bookingId, { photos_inspection: updated });
  };

  const saveOdometerReading = async (e) => {
    await updateBooking(bookingId, { odometer_reading: e.target.value });
  };

  const saveNote = async (e) => {
    await updateBooking(bookingId, { adminNote: e.target.value });
  };

  const handleGenerateCaption = async () => {
    setGeneratingCaption(true);
    setCaption('');
    try {
      const toBase64 = (url) => fetch(url).then(r => r.blob()).then(blob => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      }));
      const [beforePhoto, afterPhoto] = await Promise.all([
        photos.before[0] ? toBase64(photos.before[0]) : Promise.resolve(null),
        photos.after[0] ? toBase64(photos.after[0]) : Promise.resolve(null),
      ]);
      const vehicleStr = [booking.year, booking.make, booking.model].filter(Boolean).join(' ');
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'caption',
          beforePhoto,
          afterPhoto,
          serviceType: booking.tier || booking.service_type || 'Interior Detail',
          vehicleType: vehicleStr,
          notes: booking.adminNote || '',
        }),
      });
      const data = await res.json();
      setCaption(data.caption || '');
    } catch (err) {
      setCaption('Caption generation failed. Try again.');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const handleSummarizeNotes = async () => {
    const raw = notesRef.current?.value?.trim();
    if (!raw) return;
    setSummarizingNotes(true);
    try {
      const vehicleStr = [booking.year, booking.make, booking.model].filter(Boolean).join(' ');
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'summarize',
          rawNotes: raw,
          serviceType: booking.tier || booking.service_type || 'Detail',
          vehicleType: vehicleStr,
          clientName: booking.name || '',
        }),
      });
      const data = await res.json();
      if (notesRef.current) notesRef.current.value = data.summary;
      await updateBooking(bookingId, { adminNote: data.summary });
    } catch {
      // keep original notes on failure
    } finally {
      setSummarizingNotes(false);
    }
  };

  const handleDateSave = async () => {
    if (!dateInput) return;
    const iso = new Date(dateInput).toISOString();
    await updateBooking(bookingId, { scheduled_at: iso });
    setBooking(prev => ({ ...prev, scheduled_at: iso }));
    setEditingDate(false);
  };

  const handleSendInvoice = async () => {
    setInvoicing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch('/api/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bookingId }),
      });
      const data = await res.json();
      if (res.ok) {
        setInvoiceUrl(data.invoiceUrl);
        setInvoiceStatus(data.status);
      } else {
        alert(`Invoice error: ${data.error}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setInvoicing(false);
    }
  };

  const handleResendInvoice = async () => {
    setResending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch('/api/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bookingId }),
      });
      const data = await res.json();
      if (res.ok) {
        setInvoiceUrl(data.invoiceUrl);
        setInvoiceStatus(data.status);
        showToast('Invoice resent successfully.');
      } else {
        showToast(`Resend failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
      </div>
    );
  }

  if (!booking) {
    return <div className="p-6 text-gray-500">Job not found.</div>;
  }

  const scheduledDate = booking.scheduled_at ? toET(new Date(booking.scheduled_at)) : null;

  const depositPaid = Number(booking.deposit_amount) || 0;
  const totalPrice = Number(booking.price) || 0;
  const remainingBalance = totalPrice - depositPaid;
  const showRemainingBalance =
    depositPaid > 0 &&
    booking.status !== 'completed' &&
    invoiceStatus !== 'paid';

  // The client-facing pay page (tip selection + "what was done") — not the bare
  // Stripe hosted invoice, so clients always land somewhere they can tip.
  const payPageUrl = `${window.location.origin}/pay/${bookingId}`;

  const handleShare = async () => {
    if (!invoiceUrl) return;
    const shareData = {
      title: `Invoice — ${booking.client_name}`,
      text: `Here's your invoice for ${booking.tier || booking.service_type}.`,
      url: payPageUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) {
        if (err.name !== 'AbortError') navigator.clipboard.writeText(payPageUrl).then(() => alert('Link copied!'));
      }
    } else {
      navigator.clipboard.writeText(payPageUrl).then(() => alert('Invoice link copied to clipboard!'));
    }
  };

  const isMaintenanceBooking = booking.booking_type === 'maintenance';

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-6">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/admin/bookings" className="text-gray-400 hover:text-gray-700">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">{booking.client_name}</h2>
          <p className="text-gray-500 text-sm">{booking.tier || booking.service_type} · {scheduledDate ? format(scheduledDate, 'MMMM d, yyyy') : '—'}</p>
        </div>
        <select
          value={booking.status || 'scheduled'}
          disabled={saving}
          onChange={e => handleStatusChange(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C] bg-white"
        >
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>

        {invoiceUrl ? (
          <div className="flex items-center gap-2 flex-wrap">
            {invoiceStatus === 'paid' && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
                <CheckCircle size={15} /> Paid
              </span>
            )}
            <a
              href={invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm border border-blue-300 text-blue-700 bg-white px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <FileText size={15} /> View Invoice
            </a>
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 text-sm border border-gray-200 text-gray-600 bg-white px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              title="Share invoice link"
            >
              <Share2 size={15} /> Share
            </button>
            {booking.phone && (
              <a
                href={invoiceSmsHref(booking, payPageUrl)}
                className="flex items-center gap-1.5 text-sm border border-gray-200 text-gray-600 bg-white px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                title="Opens your Messages app with the invoice link pre-filled — just hit send"
              >
                <MessageSquare size={15} /> Text Client
              </a>
            )}
            <button
              onClick={handleResendInvoice}
              disabled={resending}
              className="flex items-center gap-1.5 text-sm border border-gray-200 text-gray-600 bg-white px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              title="Resend invoice to client"
            >
              {resending ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {resending ? 'Sending…' : 'Resend'}
            </button>
            {invoiceStatus !== 'paid' && (
              <button
                onClick={() => { setRedoInvoice(true); setShowCompleteModal(true); }}
                className="flex items-center gap-1.5 text-sm border border-amber-300 text-amber-700 bg-white px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors"
                title="Wrong amount? Void this invoice and rebuild it"
              >
                <AlertTriangle size={15} /> Fix Amount
              </button>
            )}
          </div>
        ) : (
          <button
            onClick={handleSendInvoice}
            disabled={invoicing}
            className="flex items-center gap-1.5 text-sm bg-[#C9A84C] text-black px-3 py-1.5 rounded-lg font-semibold hover:bg-[#b8943d] disabled:opacity-50 transition-colors"
          >
            {invoicing ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
            {invoicing ? 'Sending…' : 'Send Invoice'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
          <h3 className="font-semibold text-gray-800">Service Details</h3>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['Service', booking.tier || booking.service_type],
              ['Price', `$${booking.price || 0}`],
              ['Address', booking.address || '—'],
              ['Add-Ons', booking.add_ons || '—'],
            ].map(([label, val]) => (
              <div key={label}>
                <dt className="text-gray-400 text-xs">{label}</dt>
                <dd className="font-medium text-gray-800 mt-0.5">{val}</dd>
              </div>
            ))}
            {showRemainingBalance && (
              <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <dt className="text-amber-700 text-xs font-semibold uppercase tracking-wider">Remaining Balance</dt>
                  {depositPaid > 0 && (
                    <p className="text-xs text-amber-600 mt-0.5">${depositPaid.toFixed(2)} deposit already paid</p>
                  )}
                </div>
                <dd className="text-xl font-bold text-amber-700">${remainingBalance.toFixed(2)}</dd>
              </div>
            )}
            <div className="col-span-2">
              <dt className="text-gray-400 text-xs mb-1">Scheduled Date</dt>
              {editingDate ? (
                <div className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    defaultValue={dateInput}
                    onChange={e => setDateInput(e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  />
                  <button onClick={handleDateSave} className="text-xs bg-[#C9A84C] text-black px-3 py-1 rounded-lg font-semibold">Save</button>
                  <button onClick={() => setEditingDate(false)} className="text-xs text-gray-400 hover:text-gray-700">Cancel</button>
                </div>
              ) : (
                <dd className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">
                    {scheduledDate ? format(scheduledDate, 'MMM d, yyyy · h:mm a') : '—'}
                  </span>
                  <button
                    onClick={() => {
                      const val = scheduledDate
                        ? format(scheduledDate, "yyyy-MM-dd'T'HH:mm")
                        : '';
                      setDateInput(val);
                      setEditingDate(true);
                    }}
                    className="text-xs text-[#C9A84C] hover:underline"
                  >
                    {scheduledDate ? 'Edit' : 'Set date'}
                  </button>
                </dd>
              )}
            </div>
            {booking.scheduled_at && booking.address && (
              <div className="col-span-2">
                <WeatherForecast
                  scheduledAt={booking.scheduled_at}
                  address={booking.address}
                />
              </div>
            )}
          </dl>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Client & Vehicle</h3>
            {booking.client_id && (
              <Link to={`/admin/clients/${booking.client_id}`} className="text-xs text-[#C9A84C] hover:underline flex items-center gap-1">
                Profile <ExternalLink size={11} />
              </Link>
            )}
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['Name', booking.client_name],
              ['Email', booking.email],
              ['Phone', booking.phone],
              ['Vehicle', `${booking.year || ''} ${booking.make || ''} ${booking.model || ''}`.trim() || '—'],
            ].map(([label, val]) => (
              <div key={label}>
                <dt className="text-gray-400 text-xs">{label}</dt>
                <dd className="font-medium text-gray-800 mt-0.5">{val || '—'}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Pre-Inspection Card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-semibold text-gray-800">Pre-Inspection</h3>
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
              <AlertTriangle size={10} /> Car must be ON for odometer + dash
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Odometer */}
            <div className="space-y-3">
              <InspectionDropzone
                bookingId={bookingId}
                category="odometer"
                label="Odometer"
                hint="Car ON"
                icon={<Gauge size={15} />}
                photos={inspectionPhotos.odometer}
                onUpload={handleInspectionUpload}
                onDelete={handleInspectionDelete}
                maxPhotos={2}
              />
              <div>
                <label className="text-xs text-gray-400 block mb-1">Mileage Reading</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    defaultValue={odometerReading}
                    onBlur={saveOdometerReading}
                    onChange={e => setOdometerReading(e.target.value)}
                    placeholder="e.g. 47,312"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">mi</span>
                </div>
              </div>
            </div>

            {/* Dashboard display */}
            <InspectionDropzone
              bookingId={bookingId}
              category="dash"
              label="Dashboard Display"
              hint="Car ON"
              icon={<Monitor size={15} />}
              photos={inspectionPhotos.dash}
              onUpload={handleInspectionUpload}
              onDelete={handleInspectionDelete}
              maxPhotos={2}
            />

            {/* Vehicle imperfections */}
            <InspectionDropzone
              bookingId={bookingId}
              category="damage"
              label="Vehicle Imperfections"
              icon={<AlertTriangle size={15} />}
              photos={inspectionPhotos.damage}
              onUpload={handleInspectionUpload}
              onDelete={handleInspectionDelete}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <PhotoDropzone bookingId={bookingId} phase="before" photos={photos.before} onUpload={handleUpload} onDelete={handleDelete} />
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <PhotoDropzone bookingId={bookingId} phase="after" photos={photos.after} onUpload={handleUpload} onDelete={handleDelete} />
        </div>

        {/* AI Caption Generator */}
        {(photos.before.length > 0 || photos.after.length > 0) && (
          <div className="bg-white rounded-2xl p-5 shadow-sm lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-800">Caption Generator</h3>
                <p className="text-xs text-gray-400 mt-0.5">AI writes your Instagram/TikTok caption from the before & after photos</p>
              </div>
              <button
                onClick={handleGenerateCaption}
                disabled={generatingCaption}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#111827] text-white hover:bg-[#1f2937] disabled:opacity-50 transition-all"
              >
                {generatingCaption ? <><span className="animate-spin">⟳</span> Generating…</> : '✦ Generate Caption'}
              </button>
            </div>
            {caption && (
              <div className="relative">
                <textarea
                  value={caption}
                  onChange={e => setCaption(e.target.value)}
                  rows={8}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C9A84C] resize-none"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(caption); setToast({ msg: 'Caption copied!', type: 'success' }); }}
                  className="absolute top-2 right-2 px-3 py-1 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        )}

        {/* Job Timer */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Timer size={16} className="text-[#C9A84C]" /> Detail Timer
            </h3>
            <div className="flex items-center gap-3">
              {booking.timer_started_at && !editingTimer && (
                <button
                  onClick={openTimerEdit}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#C9A84C] transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
              )}
              {booking.timer_started_at && !editingTimer && (
                <button
                  onClick={handleResetTimer}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  <RotateCcw size={12} /> Reset
                </button>
              )}
            </div>
          </div>

          {editingTimer ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Start Time</label>
                  <input
                    type="time"
                    value={timerStartInput}
                    onChange={e => setTimerStartInput(e.target.value)}
                    className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">End Time</label>
                  <input
                    type="time"
                    value={timerEndInput}
                    onChange={e => setTimerEndInput(e.target.value)}
                    className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveTimerEdit}
                  className="flex-1 py-2.5 bg-[#C9A84C] text-black rounded-xl font-bold text-sm hover:bg-[#b8943d] transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingTimer(false)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
          <>
          <div className="text-center mb-4">
            <p className={`text-5xl font-black tabular-nums tracking-tight ${
              booking.timer_started_at && !booking.timer_ended_at
                ? 'text-[#C9A84C]'
                : booking.timer_ended_at
                ? 'text-gray-800'
                : 'text-gray-300'
            }`}>
              {formatElapsed(elapsed) || '0:00'}
            </p>
            {booking.timer_ended_at && (
              <p className="text-xs text-gray-400 mt-1">
                Finished · {Math.round(elapsed / 60)} min total
              </p>
            )}
            {booking.timer_started_at && !booking.timer_ended_at && (
              <p className="text-xs text-[#C9A84C] mt-1 animate-pulse">Timer running…</p>
            )}
          </div>

          {!booking.timer_started_at ? (
            <button
              onClick={handleStartTimer}
              className="w-full py-3 bg-[#C9A84C] text-black rounded-xl font-bold text-sm hover:bg-[#b8943d] transition-colors flex items-center justify-center gap-2"
            >
              <Timer size={16} /> Start Timer
            </button>
          ) : !booking.timer_ended_at ? (
            <button
              onClick={handleStopTimer}
              className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              <StopIcon size={16} /> Stop Timer
            </button>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center">
              <p className="text-emerald-700 font-semibold text-sm">Detail Complete</p>
              <p className="text-emerald-600 text-xs mt-0.5">
                Started {format(new Date(booking.timer_started_at), 'h:mm a')} ·
                Ended {format(new Date(booking.timer_ended_at), 'h:mm a')}
              </p>
            </div>
          )}
          </>
          )}
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">Completion Checklist</h3>
          <ul className="space-y-2">
            {CHECKLIST.map(item => (
              <li key={item}>
                <button
                  onClick={() => toggleCheck(item)}
                  className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#C9A84C] transition-colors w-full text-left"
                >
                  {checklist[item]
                    ? <CheckSquare size={16} className="text-[#C9A84C] shrink-0" />
                    : <Square size={16} className="text-gray-300 shrink-0" />}
                  <span className={checklist[item] ? 'line-through text-gray-400' : ''}>{item}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3">
            {Object.values(checklist).filter(Boolean).length}/{CHECKLIST.length} completed
          </p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Job Notes</h3>
            <button
              onClick={handleSummarizeNotes}
              disabled={summarizingNotes}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#111827] text-white hover:bg-[#1f2937] disabled:opacity-50 transition-all"
            >
              {summarizingNotes ? <><span className="animate-spin">⟳</span> Cleaning up…</> : '✦ Clean Up Notes'}
            </button>
          </div>
          <textarea
            ref={notesRef}
            defaultValue={booking.adminNote || ''}
            onBlur={saveNote}
            rows={6}
            placeholder="Dump your raw notes here — voice-to-text, quick thoughts, anything. Hit Clean Up Notes to turn it into a professional summary."
            className="w-full bg-white text-gray-900 text-sm border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#C9A84C] resize-none"
          />
        </div>

        {/* Client note + photos submitted at booking */}
        {(booking.customer_note || booking.customer_photos) && (() => {
          const clientPhotos = (() => {
            try { return JSON.parse(booking.customer_photos || '[]'); } catch { return []; }
          })();
          if (!booking.customer_note && !clientPhotos.length) return null;
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 shadow-sm lg:col-span-2">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-amber-500">📋</span> Client Submission
              </h3>
              {booking.customer_note && (
                <p className="text-sm text-gray-700 mb-4 italic">"{booking.customer_note}"</p>
              )}
              {clientPhotos.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Photos from Client ({clientPhotos.length})
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {clientPhotos.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity">
                        <img src={url} alt={`Client submission ${i + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {showCompleteModal && booking && (
        isMaintenanceBooking ? (
          <MaintenanceInvoiceModal
            booking={booking}
            onConfirm={handleCompleteWithInvoice}
            onSkip={handleCompleteWithoutInvoice}
            onCancel={() => { setShowCompleteModal(false); setRedoInvoice(false); }}
          />
        ) : (
          <SimpleInvoiceModal
            booking={booking}
            onConfirm={handleCompleteWithInvoice}
            onSkip={handleCompleteWithoutInvoice}
            onCancel={() => { setShowCompleteModal(false); setRedoInvoice(false); }}
          />
        )
      )}
    </div>
  );
}
